import type { ReactNode } from "react";

import { cn } from "@/lib/utils";

import { markBackground } from "./tone";

/**
 * The key to a series: a dot, a small square when the series is hatched, or a
 * dashed stroke for a comparison line (the previous period).
 */
export function Swatch({
  color,
  hatched = false,
  dashed = false,
  className,
}: {
  color: string;
  hatched?: boolean;
  dashed?: boolean;
  className?: string;
}) {
  if (dashed) {
    return (
      <span
        aria-hidden="true"
        className={cn("inline-block w-3 shrink-0 border-t-[1.5px] border-dashed", className)}
        style={{ borderColor: color }}
      />
    );
  }

  return (
    <span
      aria-hidden="true"
      className={cn(
        "inline-block shrink-0",
        hatched ? "size-2.5 rounded-xs" : "size-2 rounded-full",
        className,
      )}
      style={{ background: markBackground(color, hatched) }}
    />
  );
}

export interface LegendItem {
  key: string;
  label: ReactNode;
  color: string;
  hatched?: boolean;
  /** A comparison line rather than a filled series. */
  dashed?: boolean;
  /** A total for the series, said after its name. */
  value?: ReactNode;
}

/** The series of a chart in one line, each with its total when the chart has one. */
export function ChartLegend({
  items,
  trailing,
  className,
}: {
  items: LegendItem[];
  /** Pushed to the end of the line: the busiest day, a total. */
  trailing?: ReactNode;
  className?: string;
}) {
  return (
    <ul
      className={cn(
        "m-0 flex min-w-0 list-none flex-wrap items-center gap-x-3 gap-y-1 p-0 text-xs text-muted-foreground",
        className,
      )}
    >
      {items.map((item) => (
        <li key={item.key} className="flex min-w-0 items-center gap-1.5">
          <Swatch color={item.color} hatched={item.hatched} dashed={item.dashed} />
          <span className="truncate">{item.label}</span>
          {item.value !== undefined && (
            <span className="font-medium text-foreground tabular-nums">{item.value}</span>
          )}
        </li>
      ))}
      {trailing && <li className="ms-auto min-w-0 truncate tabular-nums">{trailing}</li>}
    </ul>
  );
}
